/**
 * A single action the assistant can suggest within a capability.
 */
export interface AssistantCapabilityAction {
  /** Action type */
  type: 'navigate' | 'create' | 'search';
  /** Translation key for the label */
  labelKey: string;
  /** Localized display label from the active UI language. */
  label?: string;
  /** Application route */
  route?: string;
  /** Search scope ID */
  scopeId?: string;
  /** Permission required (for validation) */
  permission?: string;
}

/**
 * A single capability entry in the assistant context.
 * Derived from search scope metadata and route information.
 */
export interface AssistantCapability {
  /** Unique identifier matching search scope id */
  id: string;
  /** Kind of capability source */
  kind: 'scope' | 'route' | 'action';
  /** Translation key for the label */
  labelKey: string;
  /** Localized display label from the active UI language. */
  label?: string;
  /** Application route */
  route?: string;
  /** Material icon */
  icon?: string;
  /** Short description */
  description?: string;
  /** Keywords for matching user intent */
  keywords: string[];
  /** Available actions for this capability */
  actions: AssistantCapabilityAction[];
}

/**
 * The full assistant context sent to the orchestrator for each request.
 */
export interface AssistantContext {
  /** Current frontend route from the user's browser */
  currentRoute: string;
  /** Detected scope ID based on the current route (if any) */
  currentScopeId?: string;
  /** User's locale string (e.g., 'en', 'de-CH') */
  locale: string;
  /** Permission-filtered capabilities available to this user */
  capabilities: AssistantCapability[];
}
